import React, { useCallback, useState } from 'react';
import {
  AddAttachmentHeaderContainer,
  AttachmentItemsContainer,
  SectionContainer,
} from '../styles';
import Text from 'v3/components/Text';
import Loader from 'v3/components/Loader';
import { OccurrenceDescriptionsContainer } from '../OccurrenceInfo/styles';
import { FaTrash, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import Button from 'v3/components/Button';
import Fallback from 'components/Fallback';
import theme from 'v3/theme';
import { OccurrenceAttachmentModal } from '../AttachmentsModal';
import { AttachmentNFCheckboxes } from './AttachmentNFCheckboxes';
import { getFileIcon, formatFileSize } from '../utils';
import {
  HeaderActionsRow,
  FallbackContainer,
  FallbackErrorText,
  FallbackEmptyText,
  AccordionContainer,
  AccordionHeader,
  FileIconWrapper,
  FileNameText,
  FileSizeText,
  AttachmentNfBadge,
  RemoveButton,
  ChevronWrapper,
  AccordionBody,
  MultiFileList,
  FileRow,
  FileRowNameText,
  RemoveAllRow,
  RemoveAllButton,
} from './styles';

export function AddAttachment({
  files,
  setFiles,
  loading,
  error = '',
  required = false,
  showDelivery = false,
  nfOptions = [],
  showNfErrors = false,
}) {
  const [showModal, setShowModal] = useState(false);

  const handleRemove = useCallback(
    (index, innerIndex = null) => {
      if (innerIndex !== null) {
        const updated = files.map((file, idx) => {
          if (idx === index) {
            const filtered = file?.file?.filter((_, i) => i !== innerIndex);
            return { ...file, file: [...filtered] };
          }
          return file;
        });
        setFiles(updated);
      } else {
        setFiles(files.filter((_, idx) => idx !== index));
      }
    },
    [files],
  );

  return (
    <>
      <OccurrenceAttachmentModal
        isOpen={showModal}
        handleClose={() => setShowModal(false)}
        setFiles={setFiles}
      />
      <SectionContainer>
        <AddAttachmentHeaderContainer>
          <Text type="header" color="dark" weight={500}>
            {required ? 'Anexos *' : 'Anexos'}
          </Text>
          <HeaderActionsRow>
            <Loader loading={loading} />
            <Button
              disabled={loading || files?.length === 20}
              size="sm"
              onClick={() => setShowModal(true)}
            >
              Adicionar anexo
            </Button>
          </HeaderActionsRow>
        </AddAttachmentHeaderContainer>
        <Fallback
          fall={files?.length === 0}
          component={
            <FallbackContainer>
              {error !== '' && required ? (
                <FallbackErrorText color={theme.colors.error}>
                  {error}
                </FallbackErrorText>
              ) : (
                <FallbackEmptyText color={theme.colors.gray}>
                  Nenhum anexo selecionado.
                </FallbackEmptyText>
              )}
            </FallbackContainer>
          }
        >
          <OccurrenceDescriptionsContainer>
            <AttachmentItemsContainer>
              {files?.map((file, index) => (
                <AttachmentItem
                  key={`${file?.file?.[0]?.name || ''}-${index}`}
                  index={index}
                  file={file}
                  handleRemove={handleRemove}
                  loading={loading}
                  showDelivery={showDelivery}
                  nfOptions={nfOptions}
                  files={files}
                  setFiles={setFiles}
                  showNfErrors={showNfErrors}
                />
              ))}
            </AttachmentItemsContainer>
          </OccurrenceDescriptionsContainer>
        </Fallback>
      </SectionContainer>
    </>
  );
}

function AttachmentItem({
  file,
  handleRemove,
  index,
  loading,
  showDelivery,
  nfOptions,
  files,
  setFiles,
  showNfErrors,
}) {
  const [isOpen, setIsOpen] = useState(true);

  const isMultiFile = (file?.file?.length || 0) > 1;
  const firstFile = file?.file?.[0];
  const totalSize = (file?.file || []).reduce((sum, f) => sum + (f?.size || 0), 0);
  const nfCount = (file?.selectedNfIds || []).length;
  const { Icon, color } = getFileIcon((firstFile?.name || '').split('.').pop().toLowerCase());

  const isExpandable = showDelivery || isMultiFile;
  const showBody = isOpen && isExpandable;

  return (
    <AccordionContainer>
      {/* Accordion header */}
      <AccordionHeader
        isExpandable={isExpandable}
        role={isExpandable ? 'button' : undefined}
        tabIndex={isExpandable ? 0 : undefined}
        onKeyDown={e => isExpandable && e.key === 'Enter' && setIsOpen(o => !o)}
        onClick={() => isExpandable && setIsOpen(o => !o)}
      >
        {/* File type icon */}
        <FileIconWrapper>
          <Icon size={18} color={color} />
        </FileIconWrapper>

        {/* File name / description */}
        <FileNameText type="regular" color="dark">
          {isMultiFile
            ? `${file.description || 'Arquivos'} (${file.file.length})`
            : file.description || firstFile?.name}
        </FileNameText>

        {/* File size */}
        {totalSize > 0 && (
          <FileSizeText type="label" color="gray">
            {formatFileSize(totalSize)}
          </FileSizeText>
        )}

        {/* NF count badge */}
        {showDelivery && (
          <AttachmentNfBadge active={nfCount > 0}>
            {nfCount > 0 ? `${nfCount} NF${nfCount > 1 ? 's' : ''}` : 'Sem NF'}
          </AttachmentNfBadge>
        )}

        {/* Remove button — single-file group */}
        {!isMultiFile && (
          <RemoveButton
            disabled={loading}
            onClick={e => {
              e.stopPropagation();
              handleRemove(index);
            }}
            title="Remover anexo"
          >
            <FaTrash color={theme.colors.danger} size={13} />
          </RemoveButton>
        )}

        {/* Chevron */}
        {isExpandable && (
          <ChevronWrapper>
            {isOpen ? <FaChevronUp size={11} /> : <FaChevronDown size={11} />}
          </ChevronWrapper>
        )}
      </AccordionHeader>

      {/* Accordion body */}
      {showBody && (
        <AccordionBody>
          {/* Individual file rows for multi-file group */}
          {isMultiFile && (
            <MultiFileList showDelivery={showDelivery}>
              {file.file.map((f, idx) => {
                const { Icon: FileIcon, color: fileColor } = getFileIcon(
                  (f?.name || '').split('.').pop().toLowerCase()
                );
                return (
                  <FileRow key={idx}>
                    <FileIconWrapper>
                      <FileIcon size={13} color={fileColor} />
                    </FileIconWrapper>
                    <FileRowNameText type="label" color="dark">
                      {f?.name}
                    </FileRowNameText>
                    <FileSizeText type="label" color="gray">
                      {formatFileSize(f?.size)}
                    </FileSizeText>
                    <RemoveButton
                      sm
                      disabled={loading}
                      onClick={() => handleRemove(index, idx)}
                      title="Remover arquivo"
                    >
                      <FaTrash color={theme.colors.danger} size={11} />
                    </RemoveButton>
                  </FileRow>
                );
              })}
              <RemoveAllRow>
                <RemoveAllButton
                  disabled={loading}
                  onClick={() => handleRemove(index)}
                >
                  <FaTrash size={11} />
                  Remover todos
                </RemoveAllButton>
              </RemoveAllRow>
            </MultiFileList>
          )}

          {/* NF checkboxes */}
          {showDelivery && (
            <AttachmentNFCheckboxes
              attachmentIndex={index}
              nfOptions={nfOptions}
              files={files}
              setFiles={setFiles}
            />
          )}
        </AccordionBody>
      )}
    </AccordionContainer>
  );
}
